import { CARD_W } from './shop-cards';
import type { FocusItem, ShopState } from './shop-types';
import { HEADER_ARMOR_BTN, HEADER_HEAL_BTN } from './shop-actions';
import { CRATE_POS, bagCellOf, bagTilePos, socketTilePos } from './shop-hangar';
import { BAG_TILE, CRATE_TILE, SOCKET_TILE } from './shop-hangar-tiles';

/*
 * UPGRADE BAY layout (docs/03, docs/09): design-space geometry + the focus
 * anchor of every item. Pure math shared by upgrade-screen.ts and spatial nav.
 */

/** Fixed design space — the whole screen scales to fill the real canvas. */
export const DESIGN_W = 1280;
export const DESIGN_H = 720;
// Layout v4: repair + armor buys live IN THE HEADER next to the vitals they
// affect; the reroll bar tops the middle column; cards left, hangar bottom,
// ship status far right, CONTINUE bottom-right with the next-sector info
export const CARDS_X = 20;
export const CARDS_Y = 84;
export const COLLECTION_X = 650;
export const COLLECTION_W = 340;
/** Reroll bar — a single full-width strip atop the middle column */
export const REROLL_Y = 84;
export const REROLL_H = 44;
/** Collection drops below the reroll bar */
export const COLLECTION_Y = REROLL_Y + REROLL_H + 12;
export const STATUS_X = 1010;
export const STATUS_W = 250;
export const STATUS_H = 456;
export const HANGAR_Y = 396; // STRETCHED up to the card column, bottom still at 688
export const HANGAR_X = 20;
/** Footer prompt dock (wordless key caps), under the hangar */
export const PROMPT_Y = 704;
export const CHIP_GRID = 56;
export const CHIPS_PER_ROW = 5;
export const COLLECTION_H = 232;
export const CHIP_ROWS = 3;

export function chipPos(i: number): [number, number] {
  const col = i % CHIPS_PER_ROW;
  const row = Math.floor(i / CHIPS_PER_ROW);
  return [COLLECTION_X + 44 + col * CHIP_GRID, COLLECTION_Y + 50 + row * CHIP_GRID];
}

/** Design-space center of a focus item — the anchor for spatial nav (#2). */
export function itemPos(item: FocusItem, s: ShopState): { x: number; y: number } {
  const maxChips = CHIPS_PER_ROW * CHIP_ROWS;
  switch (item.kind) {
    case 'card':
      return { x: CARDS_X + item.index * (CARD_W + 14) + CARD_W / 2, y: CARDS_Y + 126 };
    case 'reroll': return { x: COLLECTION_X + COLLECTION_W / 2, y: REROLL_Y + REROLL_H / 2 };
    case 'buyArmor':
      return {
        x: HEADER_ARMOR_BTN.x + HEADER_ARMOR_BTN.w / 2,
        y: HEADER_ARMOR_BTN.y + HEADER_ARMOR_BTN.h / 2,
      };
    case 'heal':
      return {
        x: HEADER_HEAL_BTN.x + HEADER_HEAL_BTN.w / 2,
        y: HEADER_HEAL_BTN.y + HEADER_HEAL_BTN.h / 2,
      };
    case 'chip': {
      const idx = s.collection.slice(0, maxChips).findIndex((e) => e.card.id === item.cardId);
      const [x, y] = chipPos(Math.max(0, idx));
      return { x, y };
    }
    case 'socket': {
      // Anchored on the bag's first row (tiles share a top edge), so ↑/↓
      // treat sockets and bag as ONE row — not "socket is below the bag"
      const p = socketTilePos(item.index);
      return { x: HANGAR_X + p.x + SOCKET_TILE.w / 2, y: HANGAR_Y + p.y + BAG_TILE.h / 2 };
    }
    case 'weapon':
    case 'bagBuy': {
      const cell = bagCellOf(item, s);
      if (cell < 0) {
        return { x: HANGAR_X + CRATE_POS.x + CRATE_TILE.w / 2, y: HANGAR_Y + CRATE_POS.y + CRATE_TILE.h / 2 };
      }
      const p = bagTilePos(cell);
      return { x: HANGAR_X + p.x + BAG_TILE.w / 2, y: HANGAR_Y + p.y + BAG_TILE.h / 2 };
    }
    case 'continue': return { x: STATUS_X + STATUS_W / 2, y: 648 };
    case 'tab': return { x: 0, y: 0 }; // compact layout only
  }
}
